// src/components/common/DataTable/TableActions.tsx

import {MoreHorizontal, Download, Trash2, Edit, Eye} from "lucide-react";
import {Menu, Transition} from "@headlessui/react";
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  FloatingPortal,
} from "@floating-ui/react";
import {Fragment} from "react";
import {clsx} from "clsx";
import {useConfirm} from "../../../hooks/common/useConfirm";
import type {DataTableAction, DataTableBulkAction} from "./DataTableTypes";
import {Button} from "../../ui";

// Single Row Actions Component
interface RowActionsProps<T = Record<string, unknown>> {
  record: T;
  index: number;
  actions: DataTableAction<T>[];
  className?: string;
}

export const RowActions = <T extends Record<string, unknown>>({
  record,
  index,
  actions,
  className = "",
}: RowActionsProps<T>) => {
  const {confirm} = useConfirm();
  const {refs, floatingStyles} = useFloating({
    placement: "bottom-end",
    whileElementsMounted: autoUpdate,
    middleware: [offset(8), flip(), shift()],
  });

  // Filter actions based on show conditions
  const visibleActions = actions.filter((action) =>
    action.show ? action.show(record, index) : true
  );

  if (visibleActions.length === 0) {
    return null;
  }

  const handleActionClick = async (action: DataTableAction<T>) => {
    // Check if action needs confirmation
    if (action.variant === "danger") {
      const confirmed = await confirm({
        title: "Confirm Action",
        message: `Are you sure you want to ${action.label.toLowerCase()}?`,
        variant: "danger",
        confirmText: action.label,
      });

      if (!confirmed) return;
    }

    action.onClick(record, index);
  };

  // If only 1-2 actions, show as buttons
  if (visibleActions.length <= 2) {
    return (
      <div className={clsx("flex items-center gap-1", className)}>
        {visibleActions.map((action) => (
          <Button
            key={action.key}
            variant={action.variant || "ghost"}
            size={action.size || "sm"}
            onClick={() => handleActionClick(action)}
            disabled={action.disabled ? action.disabled(record, index) : false}
            loading={action.loading || false}
            {...(action.tooltip ? {title: action.tooltip} : {})}
            className="px-2"
          >
            {action.icon}
          </Button>
        ))}
      </div>
    );
  }

  // If more than 2 actions, show dropdown menu
  return (
    <Menu
      as="div"
      className={clsx("relative inline-block text-left", className)}
    >
      <Menu.Button
        as={Button}
        variant="ghost"
        size="sm"
        className="px-2"
        ref={refs.setReference}
      >
        <MoreHorizontal className="w-4 h-4" />
      </Menu.Button>

      <FloatingPortal>
        <Transition
          as={Fragment}
          enter="transition ease-out duration-100"
          enterFrom="transform opacity-0 scale-95"
          enterTo="transform opacity-100 scale-100"
          leave="transition ease-in duration-75"
          leaveFrom="transform opacity-100 scale-100"
          leaveTo="transform opacity-0 scale-95"
        >
          <Menu.Items
            ref={refs.setFloating}
            style={floatingStyles}
            className="z-50 w-48 origin-top-right rounded-md bg-white dark:bg-dark-800 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
          >
            <div className="py-1">
              {visibleActions.map((action) => (
                <Menu.Item key={action.key}>
                  {({active}) => (
                    <button
                      onClick={() => handleActionClick(action)}
                      disabled={
                        action.disabled ? action.disabled(record, index) : false
                      }
                      className={clsx(
                        "group flex w-full items-center px-4 py-2 text-sm transition-colors",
                        active
                          ? "bg-gray-100 dark:bg-dark-700 text-gray-900 dark:text-white"
                          : "text-gray-700 dark:text-gray-300",
                        action.variant === "danger" &&
                          "text-danger-600 dark:text-danger-400",
                        (action.disabled
                          ? action.disabled(record, index)
                          : false) && "opacity-50 cursor-not-allowed"
                      )}
                    >
                      {action.icon && (
                        <div className="mr-3 h-4 w-4">{action.icon}</div>
                      )}
                      {action.label}
                    </button>
                  )}
                </Menu.Item>
              ))}
            </div>
          </Menu.Items>
        </Transition>
      </FloatingPortal>
    </Menu>
  );
};

// Bulk Actions Component
interface BulkActionsProps<T = Record<string, unknown>> {
  selectedRecords: T[];
  selectedKeys: React.Key[];
  actions: DataTableBulkAction<T>[];
  onClearSelection: () => void;
  className?: string;
}

export const BulkActions = <T extends Record<string, unknown>>({
  selectedRecords,
  selectedKeys,
  actions,
  onClearSelection,
  className = "",
}: BulkActionsProps<T>) => {
  const {confirm} = useConfirm();

  // Filter actions based on show conditions
  const visibleActions = actions.filter((action) =>
    action.show ? action.show(selectedRecords, selectedKeys) : true
  );

  if (selectedRecords.length === 0 || visibleActions.length === 0) {
    return null;
  }

  const handleBulkAction = async (action: DataTableBulkAction<T>) => {
    // Check if action needs confirmation
    if (action.confirmMessage || action.variant === "danger") {
      const confirmed = await confirm({
        title: "Confirm Bulk Action",
        message:
          action.confirmMessage ||
          `Are you sure you want to ${action.label.toLowerCase()} ${
            selectedRecords.length
          } items?`,
        variant: action.variant === "danger" ? "danger" : "warning",
        confirmText: action.label,
      });

      if (!confirmed) return;
    }

    action.onClick(selectedRecords, selectedKeys);
  };

  return (
    <div
      className={clsx(
        "flex items-center justify-between p-4 bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-lg",
        className
      )}
    >
      <div className="flex items-center gap-4">
        <span className="text-sm font-medium text-primary-700 dark:text-primary-300">
          {selectedRecords.length} item{selectedRecords.length > 1 ? "s" : ""}{" "}
          selected
        </span>

        <div className="flex items-center gap-2">
          {visibleActions.map((action) => (
            <Button
              key={action.key}
              variant={action.variant || "primary"}
              size="sm"
              onClick={() => handleBulkAction(action)}
              disabled={
                action.disabled
                  ? action.disabled(selectedRecords, selectedKeys)
                  : false
              }
            >
              {action.icon && <span className="mr-2">{action.icon}</span>}
              {action.label}
            </Button>
          ))}
        </div>
      </div>

      <Button
        variant="ghost"
        size="sm"
        onClick={onClearSelection}
        className="text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-200"
      >
        Clear selection
      </Button>
    </div>
  );
};

// Table Header Actions Component
interface TableHeaderActionsProps {
  title?: string;
  selectedCount?: number;
  totalCount?: number;
  onExport?: (format: "csv" | "excel" | "pdf") => void;
  onRefresh?: () => void;
  children?: React.ReactNode;
  className?: string;
}

export const TableHeaderActions: React.FC<TableHeaderActionsProps> = ({
  title,
  selectedCount = 0,
  totalCount = 0,
  onExport,
  onRefresh,
  children,
  className = "",
}) => {
  const handleExport = (format: "csv" | "excel" | "pdf") => {
    onExport?.(format);
  };

  return (
    <div
      className={clsx(
        "flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 p-4 bg-white dark:bg-dark-800 border-b border-gray-200 dark:border-dark-700",
        className
      )}
    >
      <div>
        {title && (
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">
            {title}
          </h3>
        )}
        {totalCount > 0 && (
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {selectedCount > 0 ? (
              <>
                {selectedCount} of {totalCount} items selected
              </>
            ) : (
              <>{totalCount} total items</>
            )}
          </p>
        )}
      </div>

      <div className="flex items-center gap-2">
        {children}

        {/* Export dropdown */}
        {onExport && (
          <Menu as="div" className="relative">
            <Menu.Button as={Button} variant="outline" size="sm">
              <Download className="w-4 h-4 mr-2" />
              Export
            </Menu.Button>

            <Transition
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <Menu.Items className="absolute right-0 z-10 mt-2 w-40 origin-top-right rounded-md bg-white dark:bg-dark-800 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                <div className="py-1">
                  <Menu.Item>
                    {({active}) => (
                      <button
                        onClick={() => handleExport("csv")}
                        className={clsx(
                          "group flex w-full items-center px-4 py-2 text-sm",
                          active
                            ? "bg-gray-100 dark:bg-dark-700 text-gray-900 dark:text-white"
                            : "text-gray-700 dark:text-gray-300"
                        )}
                      >
                        Export as CSV
                      </button>
                    )}
                  </Menu.Item>
                  <Menu.Item>
                    {({active}) => (
                      <button
                        onClick={() => handleExport("excel")}
                        className={clsx(
                          "group flex w-full items-center px-4 py-2 text-sm",
                          active
                            ? "bg-gray-100 dark:bg-dark-700 text-gray-900 dark:text-white"
                            : "text-gray-700 dark:text-gray-300"
                        )}
                      >
                        Export as Excel
                      </button>
                    )}
                  </Menu.Item>
                  <Menu.Item>
                    {({active}) => (
                      <button
                        onClick={() => handleExport("pdf")}
                        className={clsx(
                          "group flex w-full items-center px-4 py-2 text-sm",
                          active
                            ? "bg-gray-100 dark:bg-dark-700 text-gray-900 dark:text-white"
                            : "text-gray-700 dark:text-gray-300"
                        )}
                      >
                        Export as PDF
                      </button>
                    )}
                  </Menu.Item>
                </div>
              </Menu.Items>
            </Transition>
          </Menu>
        )}
      </div>
    </div>
  );
};

// Common action creators for reusability
export const createCommonActions = <T extends Record<string, unknown>>() => {
  return {
    view: (onView: (record: T, index: number) => void): DataTableAction<T> => ({
      key: "view",
      label: "View",
      icon: <Eye className="w-4 h-4" />,
      onClick: onView,
      variant: "ghost" as const,
    }),

    edit: (onEdit: (record: T, index: number) => void): DataTableAction<T> => ({
      key: "edit",
      label: "Edit",
      icon: <Edit className="w-4 h-4" />,
      onClick: onEdit,
      variant: "ghost" as const,
    }),

    delete: (
      onDelete: (record: T, index: number) => void
    ): DataTableAction<T> => ({
      key: "delete",
      label: "Delete",
      icon: <Trash2 className="w-4 h-4" />,
      onClick: onDelete,
      variant: "danger" as const,
    }),
  };
};

// Common bulk actions
export const createCommonBulkActions = <
  T extends Record<string, unknown>
>() => {
  return {
    bulkDelete: (
      onBulkDelete: (records: T[], keys: React.Key[]) => void
    ): DataTableBulkAction<T> => ({
      key: "bulk-delete",
      label: "Delete Selected",
      icon: <Trash2 className="w-4 h-4" />,
      onClick: onBulkDelete,
      variant: "danger" as const,
      confirmMessage:
        "Are you sure you want to delete the selected items? This action cannot be undone.",
    }),

    bulkExport: (
      onBulkExport: (records: T[], keys: React.Key[]) => void
    ): DataTableBulkAction<T> => ({
      key: "bulk-export",
      label: "Export Selected",
      icon: <Download className="w-4 h-4" />,
      onClick: onBulkExport,
      variant: "secondary" as const,
    }),
  };
};
